/* eslint-disable react/prop-types */
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { AuthCheckWrapper } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import PropertyDetails from '../components/PropertyDetails';
import LandlordLeasesDashboard from '../pages/LandlordLeasesDashboard';
import LandlordLeaseView from '../pages/LandlordLeaseView';
import { TenantWelcome } from '../pages/Onboarding/TenantWelcome';
import TenantDashboard from '../pages/TenantDashboard';
import TenantIdentityConfirmation from '../pages/TenantIdentityConfirmation';
import {
  COLANDLORD,
  EXPLAINER_LEASE_ROUTE,
  GENERIC_WAITLIST,
  LANDLORD,
  LOGIN_PATH,
  RENTAL_MANAGER_PATH,
  RENTER_HUB_ROOT,
  TENANT,
  YOUR_RENTAL,
} from '../constants/routeConstants';
import TenantLeaseView from '../pages/TenantLeaseView';
import TenantContentContainer from '../components/Containers/TenantContentContainer';
import DevLoginPage from '../pages/DevLoginPage';
import { ColandlordWelcome } from '../pages/Onboarding/ColandlordWelcome';
import ColandlordContentContainer from '../components/Containers/ColandlordContentContainer';
import ColandlordLeaseView from '../pages/ColandlordLeaseView';
import LandlordNonPdpPage from '../components/LandlordNonPdpPage';
import LeasesNotFound from '../pages/LeasesNotFound';
import GenericWaitlist from '../pages/Waitlist/GenericWaitlist';
import CreateLease from '../pages/Onboarding/CreateLease';
import ExplainerRedirect from '../pages/ExplainerRedirect';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import RenewalWizardModal from '../pages/LandlordLeaseRenewal/RenewalWizardModal';
import ManageYourLeaseModal from '../pages/LandlordLeaseRenewal/ManageYourLeaseModal';
import ErrorCatcher from '../components/ErrorCatcher';
import { RenewalPDFViewer as LandlordRenewalPDFViewer } from '../pages/LandlordLeaseRenewal/RenewalPDFViewer';
import { RenewalPDFViewer as TenantRenewalPDFViewer } from '../pages/TenantLeaseRenewal/RenewalPDFViewer';
import { TenantSummaryView } from '../pages/TenantLeaseRenewal/TenantSummaryView';
import { ColandlordWelcome as LeaseExtensionColandlordWelcome } from '../pages/ColandlordLeaseRenewal/ColandlordWelcome';
import { ColandlordSummary } from '../pages/ColandlordLeaseRenewal/ColandlordSummary';
import RenewalPDFViewer from '../pages/ColandlordLeaseRenewal/RenewalPDFViewer';
import { TenantDrivenLeaseRenewal } from '../pages/TenantDrivenLeaseRenewal';
import GenerateAndPreview from '../pages/LandlordLeaseView/GeneratedLeasePreview/GenerateAndPreview';

export type LEASING_URL_PARAMS = {
  propertyId?: string;
  leaseId: string;
  renewalId?: string;
  viewType?: string;
  nestedViewType?: string;
};

function verifyAndLogListingId(componentName, match) {
  const propertyId = match?.params?.propertyId;

  if (propertyId && propertyId.includes('_')) {
    const logData = {
      propertyId,
      routeComponent: componentName || 'Unknown',
      currentUrl: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      routeParams: match?.params || {},
      queryParams: typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams(window.location.search)) : {},
      routePath: match?.path || '',
    };

    logError({
      error: 'listing id detected in route params',
      context: logData,
      errorType: 'LISTING_ID_IN_LEASING_WEB',
    });
  }
}

const LandlordRouteWithPdp = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      verifyAndLogListingId(Component.name, props.match);

      return (
        <AuthCheckWrapper>
          <PropertyDetails>
            <Component {...props} />
          </PropertyDetails>
        </AuthCheckWrapper>
      );
    }}
  />
);

const LandlordRouteWithoutPdp = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      verifyAndLogListingId(Component.name, props.match);

      return (
        <AuthCheckWrapper>
          <LandlordNonPdpPage>
            <Component {...props} />
          </LandlordNonPdpPage>
        </AuthCheckWrapper>
      );
    }}
  />
);

const LandlordRouteWithoutContainer = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      verifyAndLogListingId(Component.name, props.match);

      return (
        <AuthCheckWrapper>
          <Component {...props} />
        </AuthCheckWrapper>
      );
    }}
  />
);

const ColandlordRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) => (
      <ColandlordContentContainer>
        <Component {...props} />
      </ColandlordContentContainer>
    )}
  />
);

const ColandlordLeaseExtensionRoute = ({ component: Component, ...rest }) => (
  <Route {...rest} render={(props) => <Component {...props} />} />
);

const TenantWrappedRoute = ({
  component: Component,
  breadcrumbLink,
  breadcrumbText,
  isLeaseRenewal = false,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => (
      <TenantContentContainer
        breadcrumbLink={breadcrumbLink}
        breadcrumbText={breadcrumbText}
        isLeaseRenewal={isLeaseRenewal}
      >
        <Component {...props} />
      </TenantContentContainer>
    )}
  />
);

const TenantWrappedRouteWithAuth = ({
  component: Component,
  breadcrumbLink = '',
  breadcrumbText = '',
  isLeaseRenewal = false,
  ...rest
}) => (
  <AuthCheckWrapper>
    <TenantWrappedRoute
      {...rest}
      component={Component}
      breadcrumbLink={breadcrumbLink}
      breadcrumbText={breadcrumbText}
      isLeaseRenewal={isLeaseRenewal}
    />
  </AuthCheckWrapper>
);

export default function getRouter(history) {
  return (
    <Router history={history}>
      <DefaultQueryProvider
        defaultOptions={{
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 5 * (60 * 1000), // 5 mins
            retry: 0,
          },
        }}
      >
        <ErrorCatcher>
          <Switch>
            {/* Begin misc routes */}
            <Route exact path={LOGIN_PATH} component={DevLoginPage} />
            <Route exact path={`*${EXPLAINER_LEASE_ROUTE}`} component={ExplainerRedirect} />
            {/* End misc routes */}

            {/* Begin Generic Waitlist routes */}
            <Route exact path={GENERIC_WAITLIST} component={GenericWaitlist} />
            {/* End Generic Waitlist routes */}

            {/* Begin Landlord Paths */}
            {/* Redirects for old routes */}
            <Redirect exact from={`*${LANDLORD.OLD_CREATE_LEASE}`} to={`*${LANDLORD.CREATE_LEASE}`} />
            <Redirect exact from={`*${LANDLORD.OLD_SELF_SERVE}`} to={`*${LANDLORD.LEASE_SELF_SERVE}`} />
            <LandlordRouteWithoutContainer exact path={`*${LANDLORD.RENEWAL}`} component={RenewalWizardModal} />
            <LandlordRouteWithoutContainer
              exact
              path={`*${LANDLORD.MANAGE_YOUR_LEASE}`}
              component={ManageYourLeaseModal}
            />
            <LandlordRouteWithoutContainer
              exact
              path={`*${LANDLORD.RENEWAL_PREVIEW}`}
              component={LandlordRenewalPDFViewer}
            />
            <LandlordRouteWithoutContainer exact path={`*${LANDLORD.LEASE_GENERATE}`} component={GenerateAndPreview} />
            <LandlordRouteWithoutContainer exact path={`*${LANDLORD.LEASE_REVIEW}`} component={GenerateAndPreview} />
            <LandlordRouteWithoutPdp exact path={`*${LANDLORD.LEASE_VIEW}`} component={LandlordLeaseView} />
            <LandlordRouteWithPdp exact path={`*${LANDLORD.LANDLORD_ROOT}`} component={LandlordLeasesDashboard} />
            <LandlordRouteWithoutPdp exact path={`*${LANDLORD.CREATE_LEASE}`} component={CreateLease} />
            {/* End Landlord Paths */}

            {/* Begin Colandlord Paths */}
            <ColandlordRoute exact path={`*${COLANDLORD.WELCOME}`} component={ColandlordWelcome} />
            <ColandlordLeaseExtensionRoute
              exact
              path={`*${COLANDLORD.LEASE_EXTENSION_WELCOME}`}
              component={LeaseExtensionColandlordWelcome}
            />
            <ColandlordLeaseExtensionRoute
              exact
              path={`*${COLANDLORD.LEASE_EXTENSION_SUMMARY}`}
              component={ColandlordSummary}
            />
            <ColandlordLeaseExtensionRoute
              exact
              path={`*${COLANDLORD.LEASE_EXTENSION_PREVIEW}`}
              component={RenewalPDFViewer}
            />
            <ColandlordRoute exact path={`*${COLANDLORD.LEASE_VIEW}`} component={ColandlordLeaseView} />
            {/* End Colandlord Paths */}

            {/* Begin Tenant Paths */}
            {/* Redirects for old camelCase tenant routes */}
            <Redirect exact from={`*${TENANT.OLD_LEASE_VIEW_CAMELCASE}`} to={`*${TENANT.OLD_LEASE_VIEW}`} />
            <Redirect exact from={`*${TENANT.OLD_WELCOME_CAMELCASE}`} to={`*${TENANT.OLD_WELCOME}`} />
            <Redirect exact from={`*${TENANT.OLD_IDENTITY_CONFIRMATION}`} to={`*${TENANT.IDENTITY_CONFIRMATION}`} />
            {/* Redirects for old /home/ routes */}
            <Redirect exact from="/renter-hub/leasing/home" to="/renter-hub/leasing" />
            <Redirect
              from="/renter-hub/leasing/home/lease/:leaseId/renewal/:renewalId/preview"
              to="/renter-hub/leasing/lease/:leaseId/renewal/:renewalId/preview"
            />
            <Redirect
              from="/renter-hub/leasing/home/lease/:leaseId/renewal/:renewalId"
              to="/renter-hub/leasing/lease/:leaseId/renewal/:renewalId"
            />
            <Redirect
              from="/renter-hub/leasing/home/lease/:leaseId/renew"
              to="/renter-hub/leasing/lease/:leaseId/renew"
            />
            <Redirect
              from="/renter-hub/leasing/home/lease/:leaseId/:nestedViewType?"
              to="/renter-hub/leasing/lease/:leaseId/:nestedViewType?"
            />
            <Route exact path={`*${TENANT.OLD_WELCOME}`}>
              <Redirect
                to={{
                  pathname: TENANT.WELCOME,
                  search: window.location.search,
                }}
              />
            </Route>
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.WELCOME}`}
              component={TenantWelcome}
              breadcrumbLink={YOUR_RENTAL}
              breadcrumbText="Back to your rental"
            />
            <TenantWrappedRoute
              exact
              path={`*${TENANT.IDENTITY_CONFIRMATION}`}
              component={TenantIdentityConfirmation}
              breadcrumbLink={RENTER_HUB_ROOT}
              breadcrumbText="Back to your leases"
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.OLD_LEASE_VIEW}`}
              component={TenantDashboard}
              breadcrumbLink={YOUR_RENTAL}
              breadcrumbText="Back to your rental"
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.TENANT_DRIVEN_LEASE_EXTENSION}`}
              component={TenantDrivenLeaseRenewal}
              breadcrumbLink={RENTER_HUB_ROOT}
              breadcrumbText="Back to your leases"
              isLeaseRenewal
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.LEASE_VIEW}`}
              component={TenantLeaseView}
              breadcrumbLink={RENTER_HUB_ROOT}
              breadcrumbText="Back to your leases"
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.LEASE_DASHBOARD}`}
              component={TenantDashboard}
              breadcrumbLink={YOUR_RENTAL}
              breadcrumbText="Back to your rental"
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.LEASE_EXTENSION_PREVIEW}`}
              component={TenantRenewalPDFViewer}
              breadcrumbLink={RENTER_HUB_ROOT}
              breadcrumbText="Back to your leases"
            />
            <TenantWrappedRouteWithAuth
              exact
              path={`*${TENANT.LEASE_EXTENSION_VIEW}`}
              component={TenantSummaryView}
              breadcrumbLink={RENTER_HUB_ROOT}
              breadcrumbText="Back to your leases"
              isLeaseRenewal
            />
            {/* End Tenant Paths */}
            {/* 404 */}
            <Route path={[`/${RENTAL_MANAGER_PATH}/*`, `${RENTER_HUB_ROOT}/*`]} component={LeasesNotFound} />
            {/* End 404 */}
          </Switch>
        </ErrorCatcher>
      </DefaultQueryProvider>
    </Router>
  );
}
